import { ColumnDef } from '@tanstack/react-table';
import { JobTableConfig, JobTableName } from '../../../redux/application/application.types';
import { CONTROL_COLUMN_IDS, JOB_TABLE_COLUMNS } from './jobTableColumns';
import { FieldVisibility } from '../../../fieldVisibility/fieldVisibility';
import { warnOnce } from '../../../fieldVisibility/warnOnce';

// Auftragstabellen — Spalten-Pipeline
//
//   jobs
//    │
//    ▼  hasMultipleValues                        (Komponente)
//   Kandidaten
//    │
//    ▼  visibility.isHidden   ← global ∪ Tabelle (Komponente)
//   Bestandteile ohne die ausgeblendeten
//    │
//    ▼  Sammelspalte ja/nein, aus den Resten     (Komponente)
//   ColumnDef[]
//    │
//    ▼  applyColumnConfig                        (HIER)
//   ausblenden → leer gewordene Gruppen weg → nach Config sortieren
//    │
//    ▼  Table
//
// Die Reihenfolge ist wesentlich: entscheidet die Komponente ÜBER die
// Sammelspalte, bevor die Config gewirkt hat, dann entfällt die Spalte, obwohl
// von ihr noch etwas übrig ist.
//
// Reine Funktionen, keine React-Abhängigkeit: die Zell-Darstellung bleibt in den
// Komponenten, hier wird nur die fertige Spaltenliste gefiltert und sortiert.
// Das hält den Eingriff in AccountShareJobs (629 Zeilen) klein.
//
// Warum nicht tanstacks eingebautes `columnVisibility`/`columnOrder`
// (@tanstack/react-table 8.7.9): `columnVisibility` könnte das Ausblenden samt
// leer gewordener Gruppenköpfe übernehmen — `buildHeaderGroups` baut die Köpfe
// aus den sichtbaren Blattspalten. `columnOrder` ordnet aber NUR Blattspalten;
// eine Gruppe als Block zu verschieben (Spec-Regel 3) geht damit nicht. Und die
// Sammelspalten-Regel braucht die Entscheidung ohnehin, BEVOR das ColumnDef
// entsteht. Der Gruppen-Durchlauf ist für die Reihenfolge also unvermeidlich —
// das Ausblenden kostet darin zwei Zeilen, ein zusätzliches Prop am gemeinsamen
// Table hätte mehr gekostet.

// ColumnDef kennt `columns` nur in der Gruppen-Variante; für die Rekursion wird
// die Liste einheitlich als „hat vielleicht id und Kinder" behandelt.
type AnyColumnDef = ColumnDef<any> & { id?: string; columns?: AnyColumnDef[] };

// Rang für Spalten, die die Config nicht nennt: sie stehen hinter allen genannten
// und behalten untereinander die Standardreihenfolge (Regel 4).
const UNNAMED_RANK = Number.MAX_SAFE_INTEGER;

// Anzeigereihenfolge aus der Config. Ausgeblendete Einträge zählen NICHT mit:
// sie werden entfernt und können deshalb keine Position tragen — sonst rückte
// eine Gruppe an die Stelle eines Mitglieds, das gar nicht mehr gezeigt wird.
function buildOrderIndex(config: JobTableConfig | undefined, columnKeys: Set<string>): Map<string, number> {
    const index = new Map<string, number>();

    (config?.columns ?? []).forEach((entry) => {
        if (entry.visible === false) return;
        // Nur Spalten tragen eine Position. Bestandteile und unbekannte Keys
        // werden übersprungen.
        //
        // Ohne diese Zeile rechnete es zwar richtig — ein Bestandteil-Key
        // verbrauchte nur einen Index, und da nur die RELATIVEN Ränge
        // verglichen werden, blieb die Reihenfolge der Spalten dieselbe. Sie
        // steht hier, weil „ein Bestandteil trägt keine Position" eine Regel
        // ist, die im Code sichtbar sein soll und nicht ein Zufall der
        // Arithmetik.
        if (!columnKeys.has(entry.key)) return;
        if (!index.has(entry.key)) index.set(entry.key, index.size);
    });

    return index;
}

// Eine vertippte Config darf die Tabelle nicht leeren: der Eintrag wird
// ignoriert und nur gemeldet.
function reportUnknownKeys(config: JobTableConfig | undefined, table: JobTableName, registryKeys: Set<string>): void {
    (config?.columns ?? []).forEach((entry) => {
        if (registryKeys.has(entry.key)) return;
        warnOnce(
            `config:${table}:${entry.key}`,
            `[jobTables] Unbekannter Spalten-Key "${entry.key}" in features.fieldVisibility.jobTables.${table}.columns — Eintrag wird ignoriert.`,
        );
    });
}

// Gruppenkopf ist, was eine `columns`-Liste trägt — auch eine leere. Die
// Komponenten reichen einen leeren Gruppenkopf durch, wenn die Config alle
// Bestandteile einer Sammelspalte weggenommen hat; er muss hier verschwinden.
function isGroup(column: AnyColumnDef): boolean {
    return Array.isArray(column.columns);
}

function collectLeaves(columns: AnyColumnDef[]): AnyColumnDef[] {
    return columns.flatMap((column) => (isGroup(column) ? collectLeaves(column.columns as AnyColumnDef[]) : [column]));
}

// Konfigurierbar ist nur, was in der Registry steht. Bedienelemente
// (Auswahl-Checkbox, Aktions-Spalte) werden weder ausgeblendet noch umsortiert —
// sie stehen in CONTROL_COLUMN_IDS und sind dort ausdrücklich benannt. Alles
// andere ohne Registry-Eintrag ist ein Versehen und wird gemeldet: sonst wäre
// eine neu ergänzte Datenspalte stillschweigend unausblendbar UND festgenagelt,
// vom Verhalten nicht von der Checkbox zu unterscheiden.
function isConfigurable(column: AnyColumnDef, columnKeys: Set<string>, table: JobTableName): boolean {
    if (isGroup(column)) {
        return (column.columns as AnyColumnDef[]).some((child) => isConfigurable(child, columnKeys, table));
    }

    if (column.id === undefined) return false;
    if (columnKeys.has(column.id)) return true;

    if (!CONTROL_COLUMN_IDS.has(column.id)) {
        warnOnce(
            `column:${table}:${column.id}`,
            `[jobTables] Spalte "${column.id}" ist in der Registry (${table}) keine Spalte — sie ist nicht konfigurierbar und bleibt an ihrem Platz. Entweder fehlt der Registry-Eintrag, oder der Key ist dort als Bestandteil einer Zelle eingetragen (partOf).`,
        );
    }

    return false;
}

function rankOf(column: AnyColumnDef, orderIndex: Map<string, number>): number {
    if (isGroup(column)) {
        const children = column.columns as AnyColumnDef[];
        if (children.length === 0) return UNNAMED_RANK;

        // Eine Gruppe rückt an die Position ihres ersten genannten Mitglieds.
        return Math.min(...children.map((child) => rankOf(child, orderIndex)));
    }

    return column.id !== undefined && orderIndex.has(column.id) ? (orderIndex.get(column.id) as number) : UNNAMED_RANK;
}

// Sortiert die konfigurierbaren Spalten untereinander — auf genau den Positionen,
// die sie in der Standardreihenfolge belegen. Bedienelemente bleiben dadurch an
// ihrem Platz, auch wenn eine Datenspalte nach vorne gezogen wird.
function sortByConfigOrder(
    columns: AnyColumnDef[],
    orderIndex: Map<string, number>,
    columnKeys: Set<string>,
    table: JobTableName,
): AnyColumnDef[] {
    const slots: number[] = [];
    const configurable: AnyColumnDef[] = [];

    columns.forEach((column, index) => {
        if (isConfigurable(column, columnKeys, table)) {
            slots.push(index);
            configurable.push(column);
        }
    });

    const sorted = configurable
        .map((column, index) => ({ column, index, rank: rankOf(column, orderIndex) }))
        // Gleicher Rang → Standardreihenfolge (stabil, unabhängig von der Engine).
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map((entry) => entry.column);

    const result = [...columns];
    slots.forEach((slot, position) => {
        result[slot] = sorted[position];
    });

    return result;
}

// Ausgeblendet wird nur, was in der Registry als SPALTE steht. Ein
// Bestandteil-Key nimmt hier nichts weg — er trifft ohnehin keine Spalten-`id`,
// und `columnKeys` hält ihn auch aus dem Reihenfolge-Index heraus.
function pruneAndSort(
    columns: AnyColumnDef[],
    visibility: FieldVisibility,
    orderIndex: Map<string, number>,
    columnKeys: Set<string>,
    table: JobTableName,
): AnyColumnDef[] {
    const kept: AnyColumnDef[] = [];

    columns.forEach((column) => {
        if (isGroup(column)) {
            const keptChildren = pruneAndSort(
                column.columns as AnyColumnDef[],
                visibility,
                orderIndex,
                columnKeys,
                table,
            );
            // Ein Gruppenkopf ohne verbleibende Kinder verschwindet mit ihnen.
            if (keptChildren.length === 0) return;
            kept.push({ ...column, columns: keptChildren });
            return;
        }

        if (column.id && columnKeys.has(column.id) && visibility.isHidden(column.id)) return;
        kept.push(column);
    });

    return sortByConfigOrder(kept, orderIndex, columnKeys, table);
}

// Generisch in TData, damit die Spaltenliste einer Komponente ihren Typ behält
// (AccountShareJobs arbeitet mit ColumnDef<JobType>[]). Die Umwege über `unknown`
// sind nötig, weil ColumnDef eine große Union ist und die Rekursion hier nur
// `id` und `columns` braucht.
export function applyColumnConfig<TData>(
    columns: ColumnDef<TData>[],
    visibility: FieldVisibility,
    config: JobTableConfig | undefined,
    table: JobTableName,
): ColumnDef<TData>[] {
    const registry = JOB_TABLE_COLUMNS[table];

    // Zwei Mengen aus einer Registry, weil zwei Fragen dahinterstehen.
    //
    // knownKeys — darf die Config diesen Key nennen? Ein Bestandteil-Key ist ein
    // gültiger Eintrag, auch wenn er keine Spalte trifft; er darf keine
    // Unbekannt-Meldung auslösen.
    //
    // columnKeys — ist dieser Key eine SPALTE? Nur solche werden ausgeblendet,
    // umsortiert und von Bedienelementen unterschieden. Ein Bestandteil hat
    // keinen Platz in der Tabelle, sondern einen in einer Zelle: stünde er hier
    // mit drin, nähme er eine ganze Spalte weg — in sharedJobs etwa
    // 'customer.name' die zusammengefasste Kunden-Zelle statt nur der
    // Namenszeile darin.
    const knownKeys = new Set(registry.map((column) => column.key));
    const columnKeys = new Set(registry.filter((column) => column.partOf === undefined).map((column) => column.key));

    reportUnknownKeys(config, table, knownKeys);

    // Kein frueher Ausstieg bei fehlender Tabellen-Config: eine portalweit
    // versteckte Angabe muss auch dann wirken, wenn DIESE Tabelle nichts nennt.
    // Ohne Config liefert die Pipeline dieselbe Liste wie vorher — ein Test
    // haelt das fest.
    const result = pruneAndSort(
        columns as unknown as AnyColumnDef[],
        visibility,
        buildOrderIndex(config, columnKeys),
        columnKeys,
        table,
    );

    return result as unknown as ColumnDef<TData>[];
}

// Bleibt in der fertigen Spaltenliste keine gruppierbare Spalte übrig, entfällt
// im Hinweistext über Tabelle A der Gruppieren-Teil.
//
// Die Gruppierbarkeit steht am ColumnDef (`enableGrouping: false`), nicht in der
// Registry. Ein Registry-Feld wäre eine zweite Wahrheit, die jemand gleich
// halten müsste — und wenn sie auseinanderliefe, würde der Hinweistext lügen,
// ohne dass ein Test es merkt.
export function hasGroupableColumn<TData>(columns: ColumnDef<TData>[]): boolean {
    return collectLeaves(columns as unknown as AnyColumnDef[]).some(
        (column) => (column as { enableGrouping?: boolean }).enableGrouping !== false,
    );
}

// Bleibt in der fertigen Spaltenliste eine DATENspalte übrig? Bedienelemente
// (Auswahl-Checkbox, Aktions-Spalte) zählen nicht mit: sie überleben jede
// Config, eine Tabelle nur aus ihnen ist trotzdem leer.
//
// Bewusst nicht `columns.length === 0`: das wäre in jobSelection nie wahr,
// weil dort immer eine select-Spalte mitläuft.
export function hasDataColumn<TData>(columns: ColumnDef<TData>[]): boolean {
    return collectLeaves(columns as unknown as AnyColumnDef[]).some(
        (column) => column.id !== undefined && !CONTROL_COLUMN_IDS.has(column.id),
    );
}
